import { MenuItem, TextField, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
  EscalationRule,
  formatResponseExpectation,
  HolidayWindow,
  matchEscalationRule,
  SERVICE_LEVEL_PRIORITY_ORDER,
  TimeWindow,
} from '@provision/sidekick-utils';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';

interface RuleMatchPreviewProps {
  rules: EscalationRule[];
  serviceLevel: string;
  onServiceLevelChange: (value: string) => void;
  currentTime: Dayjs;
  onCurrentTimeChange: (value: Dayjs) => void;
  timezone?: string;
  /**
   * Partner's actual business hours. Used for deadline calculations (e.g., "by end of day").
   */
  businessHours?: TimeWindow[];
  /**
   * Hours during which partners accept escalation notifications.
   * Used for condition matching. Falls back to businessHours when not set.
   */
  escalationCutoffHours?: TimeWindow[];
  holidays?: HolidayWindow[];
}

export const RuleMatchPreview = ({
  rules,
  serviceLevel,
  onServiceLevelChange,
  currentTime,
  onCurrentTimeChange,
  timezone,
  businessHours,
  escalationCutoffHours,
  holidays,
}: RuleMatchPreviewProps) => {
  const previewResult = useMemo(() => {
    if (rules.length === 0) {
      return null;
    }

    const match = matchEscalationRule(rules, serviceLevel, {
      currentTime: currentTime.toDate(),
      timezone,
      businessHours,
      escalationCutoffHours,
      holidays,
    });
    return {
      expectation: formatResponseExpectation(match, serviceLevel, timezone),
      matchedRuleName: match?.rule.name ?? match?.rule.id ?? null,
    };
  }, [rules, serviceLevel, currentTime, timezone, businessHours, escalationCutoffHours, holidays]);

  if (rules.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          fontSize: '0.6875rem',
          color: 'text.secondary',
          mb: 1,
        }}
      >
        Rule Preview
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          select
          label="Service Level"
          size="small"
          value={serviceLevel}
          onChange={(event) => onServiceLevelChange(event.target.value)}
          sx={{ minWidth: 200 }}
        >
          {SERVICE_LEVEL_PRIORITY_ORDER.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ minWidth: 220 }}>
          <DateTimePicker
            label="Time"
            value={currentTime}
            onChange={(value) => {
              if (value && dayjs(value).isValid()) {
                onCurrentTimeChange(value);
              }
            }}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Box>
      </Stack>

      {previewResult ? (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {previewResult.expectation}
          </Typography>
          {previewResult.matchedRuleName ? (
            <Typography variant="caption" color="text.secondary">
              Rule matched: &ldquo;{previewResult.matchedRuleName}&rdquo;
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};
